/**
 * Feature Flags: Feature definition methods.
 */

import { followAxiosLink } from '../..';
import { createDetailsError } from '../../../error/DetailsError';
import { getLogger } from '../../../logging';
import { MAX_LIMIT } from '../../utils';
import { createLinkMap, createLogMsgFormatter, FEATURE_FLAGS_LOGGER_COMPONENT, IdType, validateId, validateIds } from './common';
import { clearEnabledCache, getAllEnabledFeatures, isFeatureEnabled } from './enabled';
import { Feature, ToggleType } from './Feature';
import { getRootLink } from './rootLinks';
const logger = getLogger(FEATURE_FLAGS_LOGGER_COMPONENT);
const L = createLogMsgFormatter('features');

/**
 * Dictionary of features, where key is the feature id and value is the corresponding feature.
 */

const LIMIT = MAX_LIMIT; // maximum positive value for a 16-bit signed binary integer

// *** public methods ***

/**
 * Acquires a feature definition.
 * Features are cached to prevent unnecessary network traffic.
 * @param featureId Id of the desired feature to acquire.
 * @returns Readonly feature with the given id.  If the feature is not found, or
 * if the service is down, an empty feature is returned.
 * @example
 * ```
 * await feature = getFeature('MyFeature');
 * ```
 */
export async function getFeature(featureId) {
  validateId(featureId, IdType.Feature);
  const [, featureInfoMap] = await getAndCacheAllFeatures();
  const info = featureInfoMap[featureId];
  return info ? info.feature : createFeature(featureId);
}
/**
 * Acquires multiple feature definitions.
 * Features are cached to prevent unnecessary network traffic.
 * @param featureIds Variable argument list of ids of the desired features to acquire.
 * @returns Dictionary where keys are the provided feature ids and values are feature objects.
 * If a feature is not found, an empty feature is returned.
 * If the service is down, all returned features will be empty features.
 * @example
 * ```
 * await featureDict = getFeatures('MyFeature1', 'MyFeature2');
 * const str = featureDict.MyFeature1.label;
 * ```
 */
export async function getFeatures(...featureIds) {
  validateIds(featureIds, IdType.Feature);
  const [, featureInfoMap] = await getAndCacheAllFeatures();
  return featureIds.reduce((map, featureId) => {
    const info = featureInfoMap[featureId];
    map[featureId] = info ? info.feature : createFeature(featureId);
    return map;
  }, {});
}

/**
 * Acquire all known features.
 * Features are cached to prevent unnecessary network traffic.
 * @param appIdFilter Optional application id filter.  If supplied, only features associated
 * with the given application id will be returned.
 * @returns Array of features that exist in the service, possibly filtered by
 * appIdFilter.
 * If the service is down, an empty array is returned.
 * @example
 * ```
 * await features = getAllFeatures('SASDrive');
 * features.forEach((feature) => { ... });
 * ```
 */
export async function getAllFeatures(appIdFilter) {
  let [featureInfos] = await getAndCacheAllFeatures();
  if (appIdFilter !== undefined) {
    featureInfos = featureInfos.filter(info => info.feature.products.some(product => product.appRegistryId === appIdFilter));
  }
  return featureInfos.map(info => info.feature);
}

/**
 * Approves an experimental feature for use, if applicable.
 * Experimental features must be approved before an environment may use them.
 * User must have authorization to approve the feature.
 * @param featureId Id of feature to approve.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#approve}
 */
export async function approveFeature(featureId) {
  return doActionAndUpdateCache(featureId, 'approve', 'addApproval', info => info?.feature.isApproved === true);
}

/**
 * Revokes (and therefore disables) an experimental feature, if applicable.
 * Experimental features must retain approval while an environment uses them.
 * User must have authorization to revoke the feature.
 * @param featureId Id of feature to revoke and disable.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#revoke}
 */
export async function revokeFeature(featureId) {
  const resp = await doAction(featureId, 'revoke', 'deleteApproval', info => info?.feature.isApproved === false);
  if (resp === undefined) {
    // action was ignored, so return the unchanged feature
    return await getFeature(featureId);
  }
  const info = await getAndCacheOneFeature(featureId);
  return info?.feature ?? createFeature(featureId);
}

/**
 * Adds a release toggle to a feature, if applicable.
 * Typically permissioning features can have added release toggles.
 * User must have authorization to add a release toggle to the feature.
 * @param featureId Id of feature to add a release toggle.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#addReleaseToggle}
 */
export async function addFeatureReleaseToggle(featureId) {
  return doActionAndUpdateCache(featureId, 'add release toggle to', 'addReleaseToggle');
}

/**
 * Activates a feature, if applicable.
 * User must have authorization to activate the feature.
 * @param featureId Id of feature to activate.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#activate}
 */
export async function activateFeature(featureId) {
  return doActionAndUpdateCache(featureId, 'activate', 'activateFeature', info => info?.feature.isActive === true);
}

/**
 * Deactivates a feature, if applicable.
 * User must have authorization to deactivate the feature.
 * @param featureId Id of feature to deactivate.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#deactivate}
 */
export async function deactivateFeature(featureId) {
  return doActionAndUpdateCache(featureId, 'deactivate', 'deactivateFeature', info => info?.feature.isActive === false);
}

/**
 * Sets the feature's default value, if applicable.
 * User must have authorization to change the default value of the feature.
 * @param featureId Id of feature to deactivate.
 * @param defaultValue New value of the default value.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#setDefaultValue}
 */
export async function setFeatureDefaultValue(featureId, defaultValue) {
  return doActionAndUpdateCache(featureId, `change defaultValue to ${defaultValue} for`, defaultValue ? 'defaultTrue' : 'defaultFalse', info => info?.feature.defaultValue === defaultValue);
}

/**
 * Adds a user exception to a feature, if applicable.
 * User must have authorization to modify the feature's exceptions.
 * @param featureId Id of feature of which to add the exception.
 * @param userId Id of user for which to add an exception.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#addUserException}
 */
export async function addFeatureUserException(featureId, userId) {
  validateId(userId, IdType.User);
  return doActionAndUpdateCache(featureId, `add user '${userId}' exception to`, 'addIdentity', undefined, {
    params: {
      identityCollection: 'users',
      identityId: userId
    }
  });
}

/**
 * Adds a group exception to a feature, if applicable.
 * User must have authorization to modify the feature's exceptions.
 * @param featureId Id of feature of which to add the exception.
 * @param groupId Id of group for which to add an exception.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#addGroupException}
 */
export async function addFeatureGroupException(featureId, groupId) {
  validateId(groupId, IdType.Group);
  return doActionAndUpdateCache(featureId, `add group '${groupId}' exception to`, 'addIdentity', undefined, {
    params: {
      identityCollection: 'groups',
      identityId: groupId
    }
  });
}

/**
 * Removes a user exception from a feature, if applicable.
 * User must have authorization to modify the feature's exceptions.
 * @param featureId Id of feature of which to remove the exception.
 * @param userId Id of user for which to remove an exception.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#removeUserException}
 */
export async function removeFeatureUserException(featureId, userId) {
  validateId(userId, IdType.User);
  const resp = await doAction(featureId, `remove user '${userId}' exception from`, 'deleteIdentity', undefined, {
    params: {
      identityCollection: 'users',
      identityId: userId
    }
  });
  if (resp === undefined) {
    // action was ignored, so return the unchanged feature
    return await getFeature(featureId);
  }
  const info = await getAndCacheOneFeature(featureId);
  return info?.feature ?? createFeature(featureId);
}

/**
 * Removes a group exception from a feature, if applicable.
 * User must have authorization to modify the feature's exceptions.
 * @param featureId Id of feature of which to remove the exception.
 * @param groupId Id of group for which to remove an exception.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#removeGroupException}
 */
export async function removeFeatureGroupException(featureId, groupId) {
  validateId(groupId, IdType.Group);
  const resp = await doAction(featureId, `remove group '${groupId}' exception from`, 'deleteIdentity', undefined, {
    params: {
      identityCollection: 'groups',
      identityId: groupId
    }
  });
  if (resp === undefined) {
    // action was ignored, so return the unchanged feature
    return await getFeature(featureId);
  }
  const info = await getAndCacheOneFeature(featureId);
  return info?.feature ?? createFeature(featureId);
}

/**
 * Resets a feature toggle, if applicable.
 * User must have authorization to reset the feature toggle.
 * @param featureId Id of feature for which to reset the toggle.
 * @returns The new feature definition on success; otherwise the promise is rejected with a
 * DetailsError type.
 * @see {@link Feature#resetToggle}
 */
export async function resetFeatureToggle(featureId) {
  return doActionAndUpdateCache(featureId, 'reset', 'resetFeature');
}

/**
 * Deletes a feature toggle, if applicable.  Depending on the feature, this may delete
 * just a feature strategy, or it may delete the entire feature.
 * User must have authorization to delete the feature toggle.
 * @param featureId Id of feature from which to delete the toggle.
 * @returns On success, either the new feature definition should the feature
 * still exist, or undefined if the feature no longer exists; otherwise the promise is rejected with
 * a DetailsError type.
 * @see {@link Feature#deleteToggle}
 */
export async function deleteFeatureToggle(featureId) {
  const resp = await doAction(featureId, 'delete', 'delete', info => !info);
  if (resp === undefined) {
    // action was ignored, so return undefined
    return undefined;
  }
  const info = await getAndCacheOneFeature(featureId);
  return info?.feature;
}

/**
 * Query whether a user has access to create a feature on-the-fly.
 * @returns True if the user can create features on-the-fly; false otherwise.
 */
export async function canCreateFeatureOnTheFly() {
  return !!(await getRootLink('createInstantFeatureFlag'));
}

/**
 * Creates a feature on-the-fly.
 * User must have authorization to create features on-the-fly.
 * @param featureId Id of feature to create.
 * @param description Description of feature.
 * @param issueTracking Jira story or external URI which covers the new feature.
 * @param productId Optional associated application id.
 * @returns The new feature definition on success; otherwise the promise is rejected with
 * a DetailsError type.
 * @see {@link canCreateFeatureOnTheFly}
 * @example
 * ```
 * const feature = await createFeatureOnTheFly(
 *   'Sandwiches', 'Sandwiches are divine.', 'XYZZY-001', 'SASDrive');
 * ```
 */
export async function createFeatureOnTheFly(featureId, description, issueTracking, productId) {
  logger.info(L(`attempting to create feature '${featureId}' on-the-fly...`));

  // leave featureId, productId validation to the service
  const data = {
    id: featureId,
    description,
    issueTracking
  };
  if (productId !== undefined) {
    data.productId = productId;
  }
  const link = await getRootLink('createInstantFeatureFlag');
  if (!link) {
    throw new Error('This operation cannot be performed, possibly due to an authorization error.');
  }
  try {
    const resp = await followAxiosLink(link, {
      data
    });
    clearEnabledCache();
    const enabled = await isFeatureEnabled(resp.data.id);
    const info = await cacheUpdate(resp.data.id, resp.data, enabled);
    logger.info(L(`create feature '${featureId}' on-the-fly: success`));
    return info.feature;
  } catch (err) {
    logger.warn(L(err));
    logger.warn(L(`unable to create feature '${featureId}' on-the-fly`));
    throw createDetailsErrorFromAxiosError(err);
  }
}

/**
 * Enables a feature for the current user.
 * User must have authorization to toggle the feature for themselves.
 * @param featureId Id of feature to enable.
 * @returns The new feature definition on success; otherwise the promise is rejected with
 * a DetailsError type.
 * @see {@link Feature#enableForCurrentUser}
 */
export async function enableFeatureForCurrentUser(featureId) {
  function enableFeature() {
    return toggleFeature(featureId, 'enable', info => info?.feature.isEnabledForCurrentUser === true);
  }
  let feature = await enableFeature();
  if (!feature.isEnabledForCurrentUser) {
    // there's a slim chance we got out-of-sync and called a wrong toggleMe link, so try again
    // with the new toggleMe link
    feature = await enableFeature();
  }
  return feature;
}

/**
 * Disables a feature for the current user.
 * User must have authorization to toggle the feature for themselves.
 * @param featureId Id of feature to disable.
 * @returns The new feature definition on success; otherwise the promise is rejected with
 * a DetailsError type.
 * @see {@link Feature#disableForCurrentUser}
 */
export async function disableFeatureForCurrentUser(featureId) {
  function disableFeature() {
    return toggleFeature(featureId, 'disable', info => info?.feature.isEnabledForCurrentUser === false);
  }
  let feature = await disableFeature();
  if (feature.isEnabledForCurrentUser) {
    // there's a slim chance we got out-of-sync and called a wrong toggleMe link, so try again
    // with the new toggleMe link
    feature = await disableFeature();
  }
  return feature;
}

/**
 * Toggles a feature for the current user.
 * User must have authorization to toggle the feature for themselves.
 * @param featureId Id of feature to toggle.
 * @returns The new feature definition on success; otherwise the promise is rejected with
 * a DetailsError type.
 * @see {@link Feature#toggleForCurrentUser}
 */
export async function toggleFeatureForCurrentUser(featureId) {
  return toggleFeature(featureId, 'toggle');
}
async function toggleFeature(featureId, actionText, canErrorBeIgnored) {
  const resp = await doAction(featureId, actionText, 'toggleMe', canErrorBeIgnored);
  if (resp === undefined) {
    // action was ignored, so return the unchanged feature
    return await getFeature(featureId);
  }
  const info = await getAndCacheOneFeature(featureId);
  return info?.feature ?? createFeature(featureId);
}

// *** private methods ***

// Represents objects returned via /featureFlags/features.items.
/** @internal */
function createEmptySvcObj(featureId) {
  return {
    id: featureId,
    type: ToggleType.None,
    inactive: true
  };
}
function createFeatureInfo(featureId, svcObj, enabledForCurrentUser = false) {
  return {
    feature: createFeature(featureId, svcObj, enabledForCurrentUser),
    linkMap: createLinkMap(svcObj?.links)
  };
}
function createFeature(featureId, svcObj, enabledForCurrentUser = false) {
  // create feature and prevent any object mutations
  const feature = new Feature(featureId);
  if (!svcObj || featureId !== svcObj.id) {
    svcObj = createEmptySvcObj(featureId);
  }
  const linkMap = createLinkMap(svcObj.links);

  // pre-calculate strategy-related values
  const coalescedStrategies = svcObj.strategies ? svcObj.strategies.reduce((results, strategySvcObj) => {
    results.dynamicallyAdded ?? (results.dynamicallyAdded = strategySvcObj.parameters?.dynamicallyAdded); // ??=
    results.defaultValue ?? (results.defaultValue = strategySvcObj.parameters?.defaultValue); // ??=
    results.userIds ?? (results.userIds = strategySvcObj.parameters?.userIds); // ??=
    results.groupIds ?? (results.groupIds = strategySvcObj.parameters?.groupIds); // ??=
    return results;
  }, {}) : {};

  // deep copy/adjust relevant service object properties
  feature.id = featureId;
  feature.idLabel = svcObj.idLabel;
  feature.label = svcObj.label ?? svcObj.idLabel ?? featureId;
  feature.description = svcObj.description;
  feature.toggleTypes = svcObj.type?.split(',') ?? [ToggleType.None];
  feature.createdBy = svcObj.createdBy;
  feature.creationDate = svcObj.creationTimeStamp ? new Date(svcObj.creationTimeStamp) : undefined;
  feature.issueTracking = svcObj.issueTracking ? [...svcObj.issueTracking] : [];
  feature.products = svcObj.products ? svcObj.products.map(product => ({
    appRegistryId: product.appRegistryId,
    appName: product.appName,
    label: product.label
  })) : [];
  feature.isDynamicallyAddedToggle = coalescedStrategies.dynamicallyAdded === 'true';
  feature.isApprovalRequired = feature.toggleTypes.includes('experimental');
  feature.approvedBy = svcObj.approvedBy;
  feature.approvedDate = svcObj.approvedTimeStamp ? new Date(svcObj.approvedTimeStamp) : undefined;
  feature.isApproved = feature.isApprovalRequired ? feature.approvedDate !== undefined : undefined;
  feature.isDefaultValueApplicable = coalescedStrategies.defaultValue !== undefined;
  if (feature.isDefaultValueApplicable) {
    feature.defaultValue = coalescedStrategies.defaultValue === 'true';
  }
  feature.areExceptionsApplicable = typeof coalescedStrategies.userIds === 'string' || typeof coalescedStrategies.groupIds === 'string' || !!(linkMap.addIdentity || linkMap.deleteIdentity || linkMap.toggleMe);
  if (feature.areExceptionsApplicable) {
    if (typeof coalescedStrategies.userIds !== 'string' || coalescedStrategies.userIds === '') {
      feature.userExceptions = [];
    } else {
      feature.userExceptions = coalescedStrategies.userIds.split(',');
    }
    if (typeof coalescedStrategies.groupIds !== 'string' || coalescedStrategies.groupIds === '') {
      feature.groupExceptions = [];
    } else {
      feature.groupExceptions = coalescedStrategies.groupIds.split(',');
    }
  }
  feature.revision = svcObj.revision;
  feature.isActive = svcObj.inactive !== true;
  feature.isEnabledForCurrentUser = enabledForCurrentUser;
  feature.canChangeApproval = !!(linkMap.addApproval || linkMap.deleteApproval);
  feature.canAddReleaseToggle = !!linkMap.addReleaseToggle;
  feature.canChangeActivation = !!(linkMap.activateFeature || linkMap.deactivateFeature);
  feature.canChangeDefaultValue = !!(linkMap.defaultTrue || linkMap.defaultFalse);
  feature.canChangeExceptions = !!(linkMap.addIdentity || linkMap.deleteIdentity);
  feature.canResetToggle = !!linkMap.resetFeature;
  feature.canDeleteToggle = !!linkMap.delete;
  feature.canChangeEnabledStateForCurrentUser = !!linkMap.toggleMe;
  return feature.freeze();
}
let cachedFeatureInfosTuplePromise = null;

// Retrieves and caches all feature service objects.
function getAndCacheAllFeatures() {
  if (!cachedFeatureInfosTuplePromise) {
    logger.info(L('retrieving all features...'));

    // acquire the features and enabled states in parallel
    const featuresPromise = getRootLink('features').then(link => {
      if (!link) {
        throw new Error("cannot find 'features' link relation in featureFlags root request");
      }
      return followAxiosLink(link, {
        params: {
          limit: LIMIT
        }
      }).then(resp => {
        logger.info(L('received'));
        const items = resp.data?.items;
        if (!Array.isArray(items)) {
          throw new Error("cannot find 'items' array in featureFlags 'features' link request");
        }
        return items;
      });
    });
    const enabledsPromise = getAllEnabledFeatures();

    // generate feature infos tuple (infos array and info map) once all network requests are complete
    cachedFeatureInfosTuplePromise = Promise.all([featuresPromise, enabledsPromise]).then(([featureSvcObjs, enabledFeatureSet]) => {
      const infos = featureSvcObjs.map(svcObj => createFeatureInfo(svcObj.id, svcObj, enabledFeatureSet.has(svcObj.id)));
      const infoMap = infos.reduce((map, info) => {
        map[info.feature.id] = info;
        return map;
      }, {});
      return [infos, infoMap];
    }).catch(err => {
      logger.warn(L(err));
      logger.warn(L('ignoring errors and assuming no features'));

      // ignore service errors
      return [[], {}];
    });
  }
  return cachedFeatureInfosTuplePromise;
}
function getAndCacheOneFeature(featureId) {
  let infoPromise;
  clearEnabledCache();
  if (!cachedFeatureInfosTuplePromise) {
    // haven't cached features yet, so get all features and return the requested one
    infoPromise = getAndCacheAllFeatures().then(([_infos, infoMap]) => infoMap[featureId]);
  } else {
    logger.info(L(`retrieving feature '${featureId}'...`));
    const featurePromise = getRootLink('features').then(link => {
      if (!link) {
        throw new Error("cannot find 'features' link relation in featureFlags root request");
      }
      return followAxiosLink(link, {
        params: {
          ids: featureId
        }
      });
    }).then(resp => {
      logger.info(L('received'));
      const items = resp.data?.items;
      if (!Array.isArray(items)) {
        throw new Error("cannot find 'items' array in featureFlags 'features' link request");
      }
      if (items.length === 0) {
        logger.info(L(`feature '${featureId}' has been deleted`));
      }
      return items.length >= 1 ? items[0] : undefined;
    });
    const enabledStatesPromise = isFeatureEnabled(featureId);
    infoPromise = Promise.all([featurePromise, enabledStatesPromise]).then(([svcObj, enabled]) => {
      return svcObj ? cacheUpdate(featureId, svcObj, enabled) : cacheDelete(featureId);
    }).catch(err => {
      logger.warn(L(err));
      logger.warn(L(`ignoring errors and assuming feature '${featureId}' has been deleted`));

      // ignore service errors
      return cacheDelete(featureId);
    });
  }
  return infoPromise;
}
async function cacheUpdate(featureId, svcObj, enabled = false) {
  const updatedInfo = createFeatureInfo(featureId, svcObj, enabled);
  const [infos, infoMap] = await getAndCacheAllFeatures(); // should already be fulfilled

  // update item in array
  const idx = infos.findIndex(info => info.feature.id === featureId);
  if (idx >= 0) {
    infos[idx] = updatedInfo; // update
  } else {
    infos.push(updatedInfo); // add
  }

  // update or remove item from map
  infoMap[featureId] = updatedInfo; // update/add

  return updatedInfo;
}
async function cacheDelete(featureId) {
  const [infos, infoMap] = await getAndCacheAllFeatures(); // should already be fulfilled
  const idx = infos.findIndex(info => info.feature.id === featureId);
  if (idx >= 0) {
    infos.splice(idx, 1); // remove
  }

  delete infoMap[featureId]; // remove
  return undefined;
}

// Executes a link relation for a feature.
async function doActionAndUpdateCache(featureId, actionText, linkRel, canErrorBeIgnored, options) {
  const resp = await doAction(featureId, actionText, linkRel, canErrorBeIgnored, options);
  if (resp === undefined) {
    // action was ignored; return the unchanged feature
    return await getFeature(featureId);
  }

  // update cache with results of the action
  clearEnabledCache();
  const enabled = await isFeatureEnabled(featureId);
  const info = await cacheUpdate(featureId, resp.data, enabled);
  return info.feature;
}

// Executes a link relation for a feature.
async function doAction(featureId, actionText, linkRel, canErrorBeIgnored, options) {
  logger.info(L(`attempting to ${actionText} feature '${featureId}'...`));
  try {
    validateId(featureId, IdType.Feature);
    let info;
    let link;
    try {
      const wereFeaturesAlreadyCached = cachedFeatureInfosTuplePromise !== null;
      try {
        // This try-block attempts to get the feature info and action link, which could fail if
        //  a) the feature doesn't exist (missing info),
        //  b) the user isn't authorized to perform the action (missing link relation),
        //  c) the feature already had the action applied (missing link relation), or
        //  d) the feature cache is out of date (missing info or link relation).
        [info, link] = await getInfoAndLink(featureId, linkRel);
      } catch (err) {
        // In case the feature cache is out of date, retrieve the latest feature and try again.
        logger.info(L(`cache may be out of date; will retrieve latest feature '${featureId}' from service...`));

        // make sure we have the latest feature from the service
        if (!wereFeaturesAlreadyCached) {
          // turns out we *just* got the latest feature, so the error stands
          logger.warn(L(`actually we just received the latest features from the service, so the error stands`));
          throw err;
        }
        await getAndCacheOneFeature(featureId);

        // try again
        [info, link] = await getInfoAndLink(featureId, linkRel);
      }
    } catch (err) {
      // This try-block attempted to get the feature info and action link but failed.  Either
      //  a) the feature doesn't exist (missing info),
      //  b) the user isn't authorized to perform the action (missing link relation), or
      //  c) the feature already had the action applied (missing link relation).
      // If the feature already had the action applied, gracefully ignore the action.
      const [, featureInfoMap] = await getAndCacheAllFeatures();
      info = featureInfoMap[featureId];
      if (canErrorBeIgnored?.(info)) {
        logger.info(L(`${actionText} feature '${featureId}': no change necessary; ignoring action`));
        return undefined;
      }

      // Therefore
      //  a) the feature doesn't exist (missing info), or
      //  b) the user isn't authorized to perform the action (missing link relation).
      throw err;
    }

    // attempt the action
    const resp = await followAxiosLink(link, options);
    logger.info(L(`${actionText} feature '${featureId}': success`));
    return resp;
  } catch (err) {
    logger.warn(L(err));
    logger.warn(L(`unable to ${actionText} feature '${featureId}'`));
    throw createDetailsErrorFromAxiosError(err);
  }
}

// Retrieves an info tuple and link relation for a given feature id.
async function getInfoAndLink(featureId, linkRel) {
  const [, featureInfoMap] = await getAndCacheAllFeatures();
  const info = featureInfoMap[featureId];
  if (!info) {
    throw new Error(`Feature not found: ${featureId}.`);
  }
  if (typeof linkRel === 'function') {
    linkRel = linkRel(info);
  }
  const link = info.linkMap[linkRel];
  if (!link) {
    throw new Error('This operation cannot be performed, possibly due to an authorization error.');
  }
  return [info, link];
}

// Converts AxiosError to DetailsError; otherwise just returns the given argument.
function createDetailsErrorFromAxiosError(anyErr) {
  const err = anyErr; // could be anything

  // if Axios error, try to extract error message w/details from payload
  const message = err?.response?.data?.message;
  if (typeof message === 'string') {
    let details = err.response.data.details;
    if (!Array.isArray(details) || details.length < 3) {
      details = undefined;
    } else {
      // remove last two details, which resemble:
      //     path: /featureFlags/whatever
      //     correlator: some-uid
      details = details.slice(0, details.length - 2);
    }
    return createDetailsError(message, details);
  }
  return err;
}

// Invalidates the cache for the feature definition methods.
/** @internal */
export function clearFeatureDefsCache() {
  logger.info(L('clearing cache'));
  cachedFeatureInfosTuplePromise = null;
}
//# sourceMappingURL=features.js.map